/**
 * Home for a member without a talent profile — someone who came to ASK for
 * something, not to be staffed on it.
 *
 * The basic-user split (UAT 2026-09-11, #1336) took the talent surfaces out of
 * this person's sidebar and profile, and never reached the home route: they
 * still got an undismissable "Choose your Focus Areas" modal about
 * opportunities, then the talent dashboard — "Your applications", "Apply" —
 * with nothing on the page inviting the one thing they came to do (#1374).
 *
 * So the first thing here is the request, and the second is what became of the
 * ones already made. Activating a talent profile swaps this page for the
 * dashboard.
 */

import { Badge, Button, Caption1, Title2, makeStyles, tokens } from '@fluentui/react-components'
import { DocumentAdd24Regular } from '@fluentui/react-icons'
import { Link, useNavigate } from 'react-router-dom'
import { useDemands } from '../hooks/useDemands'
import { ErrorCard } from '../components/ErrorCard'
import { STAGE_LABELS } from '../lib/labels'
import type { DemandStage } from '../types/demand'
import { TitleWithHelp } from '../components/TitleWithHelp'

const RECENT = 5

const useStyles = makeStyles({
  root: { maxWidth: '720px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  sectionHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} 0`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: 'inherit',
    textDecoration: 'none',
  },
  muted: { color: tokens.colorNeutralForeground3 },
})

export function MemberHome() {
  const styles = useStyles()
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch } = useDemands({ mine_submissions: true, page_size: RECENT })
  const mine = data?.items ?? []

  return (
    <div className={styles.root} data-testid="member-home">
      <div className={styles.sectionHead}>
        <TitleWithHelp topic="submissions">
          <Title2 as="h1">Your submissions</Title2>
        </TitleWithHelp>
        <Button
          appearance="primary"
          icon={<DocumentAdd24Regular />}
          onClick={() => navigate('/submit-demand')}
          data-testid="member-home-submit"
        >
          Submit a demand
        </Button>
      </div>

      <section className={styles.section} data-testid="member-home-submissions">
        {!isError && (data?.total ?? 0) > RECENT && <Link to="/my-submissions">See all {data?.total}</Link>}
        {isLoading ? (
          <Caption1 className={styles.muted}>Loading…</Caption1>
        ) : isError ? (
          <ErrorCard
            title="Could not load submissions"
            message="Please try again."
            retry={() => void refetch()}
          />
        ) : mine.length === 0 ? (
          <Caption1 className={styles.muted}>
            No submissions yet.
          </Caption1>
        ) : (
          mine.map((d) => (
            <Link key={d.id} to={`/demand/${d.id}`} className={styles.row} data-testid={`member-home-row-${d.id}`}>
              <span>{d.title}</span>
              <Badge appearance="tint" color={d.disposition === 'digital_council' ? 'warning' : 'informative'}>
                {d.disposition === 'digital_council'
                  ? 'Passed to DSAI'
                  : (STAGE_LABELS[d.stage as DemandStage] ?? d.stage)}
              </Badge>
            </Link>
          ))
        )}
      </section>
    </div>
  )
}
